import { useStore } from 'jotai'
import { useCallback, useContext } from 'react'

import { LinkingPageContext } from '@/features/dashboard/lib/linking-page-context'
import { getSelectedInstrumentRowKey } from '@/features/order/order-form/lib/selected-instrument-identity'
import { selectionSideQuote } from '@/features/order/order-form/lib/selection-side-quote'
import { resolveOrderFormTarget } from '@/lib/resolve-order-form-target'
import { watchListTickerMapAtom } from '@/state/dashboard'
import {
  markOrderFormManual,
  type OrderFormCollapsibleConfig,
  orderFormDenomPreferenceAtomFamily,
  orderFormEntryConditionSignalAtomFamily,
  type OrderFormFieldPatch,
  orderFormFieldSignalAtomFamily,
  orderFormOrderbookSignalAtomFamily,
  type OrderFormSelection,
  orderFormSelectionAtomFamily,
  OrderFormSummaryTab,
  orderFormSummaryTabAtomFamily,
  orderFormSummaryTabPickedAtomFamily,
  singleHedgeTabAtomFamily,
  type SingleHedgeTabState,
  spreaderDismissedAtomFamily,
} from '@/state/order'
import type { SelectedInstrument, SelectedOption } from '@/types/dashboard'

/**
 * Returns setters that route writes to the Order Form in this widget's link group (the
 * focused one within it) — see computeOrderFormTarget.
 *
 * Target is resolved live (via store.get) at call-time so stale closures
 * never point to an old card ID.
 */
export function useOrderFormDispatch(myCardId: string) {
  const store = useStore()
  const pageId = useContext(LinkingPageContext)

  const setSelectedInstruments = useCallback(
    (update: SelectedInstrument[] | ((prev: SelectedInstrument[]) => SelectedInstrument[])) => {
      const targetId = resolveOrderFormTarget(store, myCardId, pageId)
      const prev = store.get(orderFormSelectionAtomFamily(targetId))
      const next = typeof update === 'function' ? update(prev.selectedInstruments) : update
      if (
        next === prev.selectedInstruments ||
        (next.length === 0 && prev.selectedInstruments.length === 0)
      )
        return
      // Keyed by (instrument identity, accountId) — NOT identity alone — so two
      // entries for the same symbol on different accounts survive instead of
      // collapsing to whichever one this call happened to write last (the
      // per-exchange account-tab filter's normalization effect, see
      // use-unified-order-form.ts, converges these onto one account shortly
      // after). Two same-symbol entries
      // that BOTH have no accountId yet still collapse (same key), which is
      // intentional: that ambiguous case is resolved by the live-sync effect
      // filling in a default account, not by keeping two indistinguishable rows.
      const seen = new Map<string, SelectedInstrument>()
      next.forEach((inst) =>
        seen.set(`${getSelectedInstrumentRowKey(inst)}:${inst.accountId ?? ''}`, inst)
      )
      store.set(orderFormSelectionAtomFamily(targetId), {
        ...prev,
        selectedInstruments: Array.from(seen.values()),
      })
      // A row from another widget (positions, watchlist…): the ticket is the user's now.
      markOrderFormManual(targetId)
    },
    [store, myCardId, pageId]
  )

  const setSelectedOptions = useCallback(
    (update: SelectedOption[] | ((prev: SelectedOption[]) => SelectedOption[])) => {
      const targetId = resolveOrderFormTarget(store, myCardId, pageId)
      const prev = store.get(orderFormSelectionAtomFamily(targetId))
      const next = typeof update === 'function' ? update(prev.selectedOptions) : update
      // Clearing options that aren't there (the chart's Esc) changes nothing — not an edit.
      if (next === prev.selectedOptions || (next.length === 0 && prev.selectedOptions.length === 0))
        return
      store.set(orderFormSelectionAtomFamily(targetId), {
        ...prev,
        selectedOptions: next,
      })
      markOrderFormManual(targetId)
    },
    [store, myCardId, pageId]
  )

  const setOrderbookPrice = useCallback(
    (price: string | null) => {
      const targetId = resolveOrderFormTarget(store, myCardId, pageId)
      const prev = store.get(orderFormOrderbookSignalAtomFamily(targetId))
      store.set(orderFormOrderbookSignalAtomFamily(targetId), {
        ...prev,
        price,
        nonce: prev.nonce + 1,
      })
    },
    [store, myCardId, pageId]
  )

  const setOrderbookAmount = useCallback(
    (amount: string | null) => {
      const targetId = resolveOrderFormTarget(store, myCardId, pageId)
      const prev = store.get(orderFormOrderbookSignalAtomFamily(targetId))
      store.set(orderFormOrderbookSignalAtomFamily(targetId), {
        ...prev,
        amount,
        nonce: prev.nonce + 1,
      })
    },
    [store, myCardId, pageId]
  )

  // Read at the action's time, even when re-adding the same ticker to an open form.
  // This writes one consumable price signal; it never subscribes to ticker updates.
  const refreshLimitPrice = useCallback(() => {
    const targetId = resolveOrderFormTarget(store, myCardId, pageId)
    const price = selectionSideQuote(
      store.get(orderFormSelectionAtomFamily(targetId)),
      store.get(watchListTickerMapAtom)
    )
    if (!price) return
    const prev = store.get(orderFormOrderbookSignalAtomFamily(targetId))
    store.set(orderFormOrderbookSignalAtomFamily(targetId), {
      ...prev,
      price,
      nonce: prev.nonce + 1,
    })
  }, [store, myCardId, pageId])

  const setOrderFormTab = useCallback(
    (tab: OrderFormSummaryTab) => {
      const targetId = resolveOrderFormTarget(store, myCardId, pageId)
      store.set(orderFormSummaryTabAtomFamily(targetId), tab)
      store.set(orderFormSummaryTabPickedAtomFamily(targetId), true)
    },
    [store, myCardId, pageId]
  )

  const setOrderFormSelection = useCallback(
    (selection: OrderFormSelection) => {
      const targetId = resolveOrderFormTarget(store, myCardId, pageId)
      store.set(orderFormSelectionAtomFamily(targetId), selection)
      markOrderFormManual(targetId)
    },
    [store, myCardId, pageId]
  )

  const setSingleHedgeState = useCallback(
    (state: SingleHedgeTabState) => {
      const targetId = resolveOrderFormTarget(store, myCardId, pageId)
      store.set(singleHedgeTabAtomFamily(targetId), state)
    },
    [store, myCardId, pageId]
  )

  // Pushing spreader-tagged legs (setSelectedInstruments/setSelectedOptions
  // with spreaderRole set) already flips isSpreaderMode on by itself once
  // both a 'quoting' and a 'taking' leg are present — this setter is only
  // needed to clear a PRIOR dismissal on the same order-form card, so a
  // freshly loaded spread doesn't stay hidden behind an earlier "back to
  // normal order" toggle.
  const setSpreaderDismissed = useCallback(
    (dismissed: boolean) => {
      const targetId = resolveOrderFormTarget(store, myCardId, pageId)
      store.set(spreaderDismissedAtomFamily(targetId), dismissed)
    },
    [store, myCardId, pageId]
  )

  // Live-signals a patch into the target order form's entry-condition
  // section (see OrderFormEntryConditionSignal's doc comment in state/order.ts
  // for why this can't be a plain `store.set` on the config atom directly —
  // useOrderFormCollapsibleConfig treats its own local state as the source of
  // truth and would clobber an external atom write on its next render).
  const setEntryConditionSignal = useCallback(
    (patch: Partial<OrderFormCollapsibleConfig>) => {
      const targetId = resolveOrderFormTarget(store, myCardId, pageId)
      const prev = store.get(orderFormEntryConditionSignalAtomFamily(targetId))
      store.set(orderFormEntryConditionSignalAtomFamily(targetId), {
        patch,
        nonce: prev.nonce + 1,
      })
    },
    [store, myCardId, pageId]
  )

  // Pre-fills react-hook-form-owned fields (TWAP duration, reduce-only…) on the target form.
  const setFieldSignal = useCallback(
    (patch: OrderFormFieldPatch) => {
      const targetId = resolveOrderFormTarget(store, myCardId, pageId)
      const prev = store.get(orderFormFieldSignalAtomFamily(targetId))
      store.set(orderFormFieldSignalAtomFamily(targetId), { patch, nonce: prev.nonce + 1 })
    },
    [store, myCardId, pageId]
  )

  // The target form's Base/Quote setting, which decides the unit a bare amount lands in.
  const getDenomPreference = useCallback(
    () =>
      store.get(
        orderFormDenomPreferenceAtomFamily(resolveOrderFormTarget(store, myCardId, pageId))
      ),
    [store, myCardId, pageId]
  )

  return {
    refreshLimitPrice,
    getDenomPreference,
    setSelectedInstruments,
    setSelectedOptions,
    setOrderFormSelection,
    setOrderFormTab,
    setSingleHedgeState,
    setOrderbookPrice,
    setOrderbookAmount,
    setSpreaderDismissed,
    setEntryConditionSignal,
    setFieldSignal,
  }
}
